import React, { useMemo } from "react";
import * as THREE from "three";

// ============================================================================
// 🌿 KURO MESH — MOSS-STONE GOLEM & CYBER-AMPHIBIAN HYBRID
// Ultra-lightweight procedural river-pebble golem with organic moss & living sprout
// ============================================================================

export interface KuroProps {
  frame: number;
  fps: number;
  isSpeaking?: boolean;
  isExcited?: boolean;
}

export const KuroMesh: React.FC<KuroProps> = ({
  frame,
  fps,
  isSpeaking = false,
  isExcited = false,
}) => {
  const t = frame / fps; // Deterministic frame-time

  // --- DYNAMICS & SQUASH-AND-STRETCH ---
  // Heavy breathing: stone expands outward when compressing vertically
  const breathCycle = Math.sin(t * 1.8) * 0.035;
  const torsoScaleY = 1.0 + breathCycle;
  const torsoScaleXZ = 1.0 - breathCycle * 0.45;

  // Sturdy grounding & idle balancing
  const idleBalance = Math.sin(t * 0.7) * 0.02;
  const excitedHop = isExcited ? Math.abs(Math.sin(t * 7.5)) * 0.18 : 0;
  const excitedShake = isExcited ? Math.sin(t * 30) * 0.02 : 0;

  // Head tilt curiosity
  const headTiltZ = Math.sin(t * 0.9) * 0.07 + (isExcited ? Math.sin(t * 5) * 0.12 : 0);
  const headTiltX = Math.cos(t * 0.6) * 0.04;

  // Sprout multi-harmonic wind physics
  const sproutWaveX = Math.sin(t * 2.8) * 0.22 + Math.cos(t * 5.2) * 0.08;
  const sproutWaveZ = Math.cos(t * 2.1) * 0.26 + Math.sin(t * 4.4) * 0.09;

  // Speaking mouth/jaw shift & eye pulse
  const jawDrop = isSpeaking ? Math.abs(Math.sin(t * 12)) * 0.045 : 0;
  const speakPulse = isSpeaking ? Math.sin(t * 14) * 0.015 : 0;
  const eyeIntensity = isSpeaking ? 1.8 + Math.sin(t * 14) * 0.6 : isExcited ? 2.4 : 1.1;

  // --- MEMOIZED PBR MATERIALS (CERAMIC STONE & BIOLUMINESCENT MOSS) ---
  const slateMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: "#3E4C59", // Smooth tactile river-slate
        roughness: 0.82,
        metalness: 0.08,
      }),
    []
  );

  const darkSlateMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: "#242F38", // Deep obsidian pebble
        roughness: 0.78,
        metalness: 0.12,
      }),
    []
  );

  const mossMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: "#2E7D32", // Lush emerald velvet moss
        roughness: 0.95,
        metalness: 0.0,
        emissive: new THREE.Color("#1B5E20"),
        emissiveIntensity: 0.35,
      }),
    []
  );

  const sproutStemMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: "#4CAF50",
        roughness: 0.6,
        metalness: 0.05,
      }),
    []
  );

  const sproutBudMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: "#69F0AE",
        roughness: 0.3,
        metalness: 0.1,
        emissive: new THREE.Color("#00E676"),
        emissiveIntensity: 0.7,
      }),
    []
  );

  const eyeVisorMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: "#00E5FF", // Cyan optic slit
        roughness: 0.1,
        metalness: 0.2,
        emissive: new THREE.Color("#00E5FF"),
        emissiveIntensity: eyeIntensity,
      }),
    [eyeIntensity]
  );

  return (
    <group position={[excitedShake, idleBalance + excitedHop, 0]}>
      {/* ── PEBBLE TORSO (MAIN MASS) ── */}
      <group scale={[torsoScaleXZ + speakPulse, torsoScaleY, torsoScaleXZ + speakPulse]}>
        {/* Core Torso (Tapered Capsule / Flattened Pebble) */}
        <mesh position={[0, 0.45, 0]} scale={[1.1, 0.9, 0.95]} castShadow receiveShadow>
          <sphereGeometry args={[0.42, 20, 16]} />
          <primitive object={slateMaterial} attach="material" />
        </mesh>

        {/* Moss Overgrowth Cluster 1 (Left Shoulder) */}
        <mesh position={[-0.26, 0.62, 0.18]} scale={[0.16, 0.1, 0.14]} castShadow receiveShadow>
          <sphereGeometry args={[1, 10, 8]} />
          <primitive object={mossMaterial} attach="material" />
        </mesh>

        {/* Moss Overgrowth Cluster 2 (Lower Right Back) */}
        <mesh position={[0.28, 0.35, -0.2]} scale={[0.18, 0.12, 0.14]} castShadow receiveShadow>
          <sphereGeometry args={[1, 10, 8]} />
          <primitive object={mossMaterial} attach="material" />
        </mesh>
      </group>

      {/* ── ARTICULATED HEAD PEBBLE ── */}
      <group position={[0, 0.88, 0.04]} rotation={[headTiltX, 0, headTiltZ]}>
        {/* Head Stone */}
        <mesh scale={[1.05, 0.88, 0.98]} castShadow receiveShadow>
          <sphereGeometry args={[0.32, 22, 18]} />
          <primitive object={darkSlateMaterial} attach="material" />
        </mesh>

        {/* Head Moss Crest */}
        <mesh position={[0.12, 0.24, -0.06]} scale={[0.14, 0.07, 0.12]} castShadow receiveShadow>
          <sphereGeometry args={[1, 10, 8]} />
          <primitive object={mossMaterial} attach="material" />
        </mesh>

        {/* Cyber-Amphibian Eye Visor Recess */}
        <mesh position={[0, 0.04, 0.28]} castShadow>
          <boxGeometry args={[0.34, 0.06, 0.06]} />
          <primitive object={darkSlateMaterial} attach="material" />
        </mesh>

        {/* Dual Glowing Eye Slits */}
        {[-0.09, 0.09].map((xOffset, i) => (
          <mesh key={`eye-optic-${i}`} position={[xOffset, 0.04, 0.31]} castShadow>
            <sphereGeometry args={[0.026, 12, 10]} />
            <primitive object={eyeVisorMaterial} attach="material" />
          </mesh>
        ))}

        {/* Stone Jaw Shift (Speaking Motion) */}
        <group position={[0, -0.16 - jawDrop, 0.16]} rotation={[jawDrop * 2.5, 0, 0]}>
          <mesh castShadow receiveShadow>
            <boxGeometry args={[0.22, 0.06, 0.16]} />
            <primitive object={darkSlateMaterial} attach="material" />
          </mesh>
        </group>

        {/* ── LIVING NATURE SPROUT / SENSOR ANTENNA ── */}
        <group position={[0, 0.28, 0]} rotation={[sproutWaveX, 0, sproutWaveZ]}>
          {/* Flexible Stem */}
          <mesh position={[0, 0.1, 0]} castShadow>
            <cylinderGeometry args={[0.012, 0.02, 0.2, 10]} />
            <primitive object={sproutStemMaterial} attach="material" />
          </mesh>
          {/* Terminal Bioluminescent Sprout Bud */}
          <mesh position={[0, 0.22, 0]} scale={[1, 1.25, 0.8]} castShadow>
            <sphereGeometry args={[0.042, 14, 12]} />
            <primitive object={sproutBudMaterial} attach="material" />
          </mesh>
          {/* Side Micro Leaf */}
          <mesh position={[0.03, 0.13, 0.02]} rotation={[0.4, 0, 0.6]} scale={[0.03, 0.06, 0.015]} castShadow>
            <sphereGeometry args={[1, 8, 6]} />
            <primitive object={sproutStemMaterial} attach="material" />
          </mesh>
        </group>
      </group>

      {/* ── STUBBY GROUNDED LIMBS ── */}
      {/* Front Left Arm */}
      <group position={[-0.34, 0.32, 0.12]} rotation={[0.2, 0, -0.35]}>
        <mesh scale={[0.11, 0.18, 0.11]} castShadow receiveShadow>
          <sphereGeometry args={[1, 12, 10]} />
          <primitive object={slateMaterial} attach="material" />
        </mesh>
      </group>
      {/* Front Right Arm */}
      <group position={[0.34, 0.32, 0.12]} rotation={[0.2, 0, 0.35]}>
        <mesh scale={[0.11, 0.18, 0.11]} castShadow receiveShadow>
          <sphereGeometry args={[1, 12, 10]} />
          <primitive object={slateMaterial} attach="material" />
        </mesh>
      </group>
      {/* Base Sturdy Foot Stone L */}
      <mesh position={[-0.22, 0.06, 0.08]} scale={[0.16, 0.09, 0.2]} castShadow receiveShadow>
        <sphereGeometry args={[1, 12, 10]} />
        <primitive object={darkSlateMaterial} attach="material" />
      </mesh>
      {/* Base Sturdy Foot Stone R */}
      <mesh position={[0.22, 0.06, 0.08]} scale={[0.16, 0.09, 0.2]} castShadow receiveShadow>
        <sphereGeometry args={[1, 12, 10]} />
        <primitive object={darkSlateMaterial} attach="material" />
      </mesh>
    </group>
  );
};

// ============================================================================
// 💡 SPARK MESH — HOVERING VACUUM-TUBE & BIOLUMINESCENT JELLY DROID
// Retro-futuristic airborne companion with glass dome, glowing filament & tendrils
// ============================================================================

export interface SparkProps {
  frame: number;
  fps: number;
  isSpeaking?: boolean;
  isSurprised?: boolean;
}

export const SparkMesh: React.FC<SparkProps> = ({
  frame,
  fps,
  isSpeaking = false,
  isSurprised = false,
}) => {
  const t = frame / fps;

  // --- AIRBORNE HOVER & DISSIPATION PHYSICS ---
  const hoverBob = Math.sin(t * 2.4) * 0.08;
  const hoverTiltX = Math.sin(t * 1.5) * 0.06;
  const hoverTiltZ = Math.cos(t * 1.8) * 0.08;

  // Surprised: sudden levitation jerk, rapid nervous oscillation, and flare
  const surpriseJerkY = isSurprised ? 0.32 + Math.sin(t * 24) * 0.025 : 0;
  const surpriseScale = isSurprised ? 1.15 : 1.0;
  const filamentPulse = isSpeaking
    ? 1.4 + Math.abs(Math.sin(t * 15)) * 1.2
    : isSurprised
    ? 2.8 + Math.sin(t * 20) * 0.6
    : 1.0 + Math.sin(t * 3.2) * 0.3;

  // 4 Ribbon / Tendril Undulation harmonics
  const tendrilWaveA = Math.sin(t * 3.8) * 0.28;
  const tendrilWaveB = Math.sin(t * 3.8 + 1.2) * 0.32;
  const tendrilWaveC = Math.sin(t * 3.8 + 2.4) * 0.28;
  const tendrilWaveD = Math.sin(t * 3.8 + 3.6) * 0.32;

  // --- MEMOIZED PBR MATERIALS (BRASS, GLASS TRANSMISSION & AMBER FILAMENT) ---
  const brassCollarMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: "#C59B27", // Brushed antique brass
        roughness: 0.25,
        metalness: 0.88,
      }),
    []
  );

  const glassDomeMaterial = useMemo(
    () =>
      new THREE.MeshPhysicalMaterial({
        color: "#E0F7FA", // Crystal clear vintage vacuum envelope
        transmission: 0.92,
        opacity: 1,
        transparent: true,
        roughness: 0.08,
        metalness: 0.05,
        ior: 1.5,
        thickness: 0.4,
      }),
    []
  );

  const filamentHotMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: "#FFF3E0",
        roughness: 0.1,
        metalness: 0.9,
        emissive: new THREE.Color("#FF8F00"), // Warm vacuum amber core
        emissiveIntensity: filamentPulse,
      }),
    [filamentPulse]
  );

  const ribbonMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: "#80DEEA", // Bioluminescent cyan-jelly floater ribbons
        roughness: 0.3,
        metalness: 0.1,
        emissive: new THREE.Color("#00ACC1"),
        emissiveIntensity: 0.6,
        transparent: true,
        opacity: 0.85,
      }),
    []
  );

  return (
    <group
      position={[0, 1.1 + hoverBob + surpriseJerkY, 0]}
      rotation={[hoverTiltX, 0, hoverTiltZ]}
      scale={[surpriseScale, surpriseScale, surpriseScale]}
    >
      {/* ── ANTIQUE BRASS BASE COLLAR & HEAT SINK ── */}
      <group position={[0, -0.06, 0]}>
        <mesh castShadow receiveShadow>
          <cylinderGeometry args={[0.26, 0.29, 0.12, 24]} />
          <primitive object={brassCollarMaterial} attach="material" />
        </mesh>
        {/* Beveled Fastener Ring */}
        <mesh position={[0, -0.07, 0]} castShadow>
          <torusGeometry args={[0.27, 0.024, 10, 24]} />
          <primitive object={brassCollarMaterial} attach="material" />
        </mesh>
      </group>

      {/* ── TRANSPARENT VACUUM DOME (TRANSMISSION GLASS) ── */}
      <mesh position={[0, 0.18, 0]} castShadow>
        <sphereGeometry args={[0.36, 24, 20, 0, Math.PI * 2, 0, Math.PI * 0.82]} />
        <primitive object={glassDomeMaterial} attach="material" />
      </mesh>

      {/* ── INNER WARM FILAMENT & COIL CORE ── */}
      <group position={[0, 0.14, 0]}>
        {/* Center Support Wire */}
        <mesh position={[0, -0.06, 0]} castShadow>
          <cylinderGeometry args={[0.012, 0.012, 0.14, 8]} />
          <primitive object={brassCollarMaterial} attach="material" />
        </mesh>
        {/* Spiraled Glowing Filament Loop */}
        <mesh castShadow>
          <torusGeometry args={[0.11, 0.024, 10, 24]} />
          <primitive object={filamentHotMaterial} attach="material" />
        </mesh>
        {/* Glowing Energy Crown / Filament Peak */}
        <mesh position={[0, 0.09, 0]} castShadow>
          <sphereGeometry args={[0.045, 12, 10]} />
          <primitive object={filamentHotMaterial} attach="material" />
        </mesh>
      </group>

      {/* ── 4 UNDULATING BIOLUMINESCENT JELLY RIBBONS / TENDRILS ── */}
      {/* Ribbon 1 (Front-Left) */}
      <group position={[-0.14, -0.12, 0.14]} rotation={[tendrilWaveA, 0, tendrilWaveB * 0.5]}>
        <mesh position={[0, -0.16, 0]} scale={[0.035, 0.32, 0.015]} castShadow>
          <boxGeometry args={[1, 1, 1]} />
          <primitive object={ribbonMaterial} attach="material" />
        </mesh>
        {/* Tail Segment */}
        <group position={[0, -0.32, 0]} rotation={[tendrilWaveB * 0.8, 0, 0]}>
          <mesh position={[0, -0.14, 0]} scale={[0.025, 0.28, 0.01]} castShadow>
            <boxGeometry args={[1, 1, 1]} />
            <primitive object={ribbonMaterial} attach="material" />
          </mesh>
        </group>
      </group>

      {/* Ribbon 2 (Front-Right) */}
      <group position={[0.14, -0.12, 0.14]} rotation={[tendrilWaveB, 0, -tendrilWaveA * 0.5]}>
        <mesh position={[0, -0.16, 0]} scale={[0.035, 0.32, 0.015]} castShadow>
          <boxGeometry args={[1, 1, 1]} />
          <primitive object={ribbonMaterial} attach="material" />
        </mesh>
        <group position={[0, -0.32, 0]} rotation={[tendrilWaveC * 0.8, 0, 0]}>
          <mesh position={[0, -0.14, 0]} scale={[0.025, 0.28, 0.01]} castShadow>
            <boxGeometry args={[1, 1, 1]} />
            <primitive object={ribbonMaterial} attach="material" />
          </mesh>
        </group>
      </group>

      {/* Ribbon 3 (Back-Left) */}
      <group position={[-0.14, -0.12, -0.14]} rotation={[-tendrilWaveC, 0, tendrilWaveD * 0.5]}>
        <mesh position={[0, -0.16, 0]} scale={[0.035, 0.32, 0.015]} castShadow>
          <boxGeometry args={[1, 1, 1]} />
          <primitive object={ribbonMaterial} attach="material" />
        </mesh>
        <group position={[0, -0.32, 0]} rotation={[tendrilWaveD * 0.8, 0, 0]}>
          <mesh position={[0, -0.14, 0]} scale={[0.025, 0.28, 0.01]} castShadow>
            <boxGeometry args={[1, 1, 1]} />
            <primitive object={ribbonMaterial} attach="material" />
          </mesh>
        </group>
      </group>

      {/* Ribbon 4 (Back-Right) */}
      <group position={[0.14, -0.12, -0.14]} rotation={[-tendrilWaveD, 0, -tendrilWaveC * 0.5]}>
        <mesh position={[0, -0.16, 0]} scale={[0.035, 0.32, 0.015]} castShadow>
          <boxGeometry args={[1, 1, 1]} />
          <primitive object={ribbonMaterial} attach="material" />
        </mesh>
        <group position={[0, -0.32, 0]} rotation={[tendrilWaveA * 0.8, 0, 0]}>
          <mesh position={[0, -0.14, 0]} scale={[0.025, 0.28, 0.01]} castShadow>
            <boxGeometry args={[1, 1, 1]} />
            <primitive object={ribbonMaterial} attach="material" />
          </mesh>
        </group>
      </group>
    </group>
  );
};
